import React, {useMemo, useRef, useEffect, useCallback} from 'react';
import {Button, Dropdown, Menu, Modal, Skeleton, Switch, Table, Tag, Tooltip} from 'antd';
import {useFileList} from '../../hooks/useFileList';
import type {FileItem, PermissionCheckParams} from '../../types/directory';
import {formatFileSize} from '../../utils/file';
import {DownloadButton} from '../DownloadButton';
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  SwapOutlined,
  FormOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  ShareAltOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import './index.scss';
import {FileThumbnail} from '../FileThumbnail';
import {buildChildPath} from '../../utils/path';
import {useFilePermission} from './useFilePermission';
import {useFileShare} from './useFileShare';
import {useMinimumVisible} from '../../hooks/useMinimumVisible';

function renderStatus(status: any, record: FileItem) {
  if (record.type === 'directory') return null;

  if (Number(status) === 2) {
    return (
      <Tag className="drive-file-list__processing-tag" color="blue">
        处理中
      </Tag>
    );
  }

  return null;
}

function getLastPathName(path: string) {
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 1] || path;
}

function renderNameShareTag(record: FileItem) {
  if (record.is_shared) {
    return (
      <span className="drive-file-list__name-share">
        <Tooltip
          overlayClassName="drive-file-list__tooltip"
          title={
            <div>
              分享人：{record.shared_by_email || '来自共享'}
            </div>
          }
        >
          <Tag className="drive-file-list__shared-tag">共享自: {record.full_path || '来自共享'}</Tag>
        </Tooltip>
      </span>
    );
  }

  const paths = record.share_info?.shared_directory_paths || [];

  if (!record.share || !paths.length) return null;

  const firstPath = paths[0];
  const hiddenCount = paths.length - 1;

  return (
    <span className="drive-file-list__name-share">
      共享到：
      <Tooltip
        title={firstPath}
        overlayClassName="drive-file-list__tooltip"
      >
        <Tag className="drive-file-list__shared-tag">
          {getLastPathName(firstPath)}
        </Tag>
      </Tooltip>

      {hiddenCount > 0 && (
        <Tooltip
          overlayClassName="drive-file-list__tooltip"
          title={
            <div className="drive-file-list__tag-tooltip">
              {paths.slice(1).map(path => (
                <Tag className="drive-file-list__tag" key={path}>{path}</Tag>
              ))}
            </div>
          }
        >
          <span className="drive-file-list__tag-more">
            +{hiddenCount}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

export function FileList({
                           parentId,
                           pageSize = 20,
                           accessPath,
                           selection,
                           onDelete,
                           onMove,
                           onRename,
                           onEnterDirectory,
                           onUpdateFileMeta,
                           onEdit,
                           onView,
                           currentPath,
                           isOrganizationLevel,
                           currentUser,
                           isMyFileLevel,
                           onDirectoryLoaded,
                           onPermissionCheck,
                           onShareScope,
                           scrollY = 'calc(100vh - 260px)'
                         }: any) {
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetching} = useFileList(parentId, pageSize, accessPath);
  const showSyncing = useMinimumVisible(isFetching && !isLoading, 600);
  const {canOperate, canUpdatePermission} = useFilePermission({currentUser, isMyFileLevel});
  const {shareFile} = useFileShare();
  const {selectedItems, selectAll} = selection;

  const list: FileItem[] = useMemo(() => {
    return data?.pages?.flatMap(page => page.items) || [];
  }, [data]);

  const checkBeforeOperate = useCallback(async (record: FileItem) => {
    if (!onPermissionCheck) return true;

    const items: PermissionCheckParams[] = [
      {
        id: record.id,
        type: record.type,
      }
    ];

    return onPermissionCheck(items);

  }, [onPermissionCheck]);

  const columns: any = useMemo(() => [
    {
      title: '名称',
      dataIndex: 'name',
      render: (_: any, record: FileItem) => (
        <div className="drive-file-list__cell">
          <FileThumbnail file={record}/>

          <div className="drive-file-list__name-content">
            <div className="drive-file-list__name-row">
              {record.type === 'file' ? (
                <Tooltip
                  title={record.name}
                  overlayClassName="drive-file-list__tooltip"
                >
                  <span
                    className="drive-file-list__ellipsis drive-file-list__preview-trigger"
                    onClick={() => onView(record)}
                  >
                  {record.name}
                  </span>
                </Tooltip>
              ) : (
                <Tooltip
                  title={record.name}
                  overlayClassName="drive-file-list__tooltip"
                >
              <span
                className="drive-file-list__ellipsis"
              >
                  {record.name}
                </span>
                </Tooltip>
              )}
              {renderStatus(record.status, record)}
            </div>
            {renderNameShareTag(record)}
          </div>
        </div>
      )
    },
    {
      title: '标签',
      dataIndex: 'tags',
      width: 220,
      render: (_: any, record: FileItem) => {
        const customTags = record.custom_tags || [];
        const autoTag = record.tags || [];
        // 自定义标签用绿色，AI标签用默认蓝色
        const allTags: Array<{ name: string; type: 'custom' | 'ai' }> = [
          ...customTags.map((t: string) => ({ name: t, type: 'custom' as const })),
          ...autoTag.map((t: string) => ({ name: t, type: 'ai' as const })),
        ];

        if (!allTags.length) return '-';

        const MAX_VISIBLE_TAGS = 4;

        const visibleTags = allTags.slice(0, MAX_VISIBLE_TAGS);
        const hiddenTags = allTags.slice(MAX_VISIBLE_TAGS);

        return (
          <div className="drive-file-list__tags">
            {visibleTags.map(({ name, type }) => (
              <Tag
                key={name + type}
                color={type === 'custom' ? 'green' : 'blue'}
                className={type === 'custom' ? 'drive-file-list__tag--custom' : 'drive-file-list__tag'}
              >
                {name}
              </Tag>
            ))}
            {hiddenTags.length > 0 && (
              <Tooltip
                overlayClassName="drive-file-list__tooltip"
                title={
                  <div className="drive-file-list__tag-tooltip">
                    {hiddenTags.map(({ name, type }) => (
                      <Tag
                        key={name + type}
                        color={type === 'custom' ? 'green' : 'blue'}
                        className={type === 'custom' ? 'drive-file-list__tag--custom' : 'drive-file-list__tag'}
                      >
                        {name}
                      </Tag>
                    ))}
                  </div>
                }
              >
                <span className="drive-file-list__tag-more">
                  +{hiddenTags.length}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    !isMyFileLevel
      ? {
        title: '读写',
        dataIndex: 'visibility',
        width: 90,
        render: (visibility: string, record: FileItem) => {
          if (record.is_shared) return '-';

          return (
            <div onClick={e => e.stopPropagation()}>
              <Switch
                checked={visibility === 'edit'}
                disabled={!canUpdatePermission(record) || !!record.is_group}
                onChange={checked => {
                  onUpdateFileMeta({
                    id: record.id,
                    description: record.description || '',
                    visibility: checked ? 'edit' : 'read',
                    custom_tags: record.custom_tags || [],
                    tags: record.tags || [],
                    type: record.type,
                  });
                }}
              />
            </div>
          );
        }
      }
      : null,
    {
      title: '描述',
      key: 'description',
      render: (_: any, record: FileItem) => {
        if (!record.description) return <div style={{minWidth: 10}}>-</div>;

        return (
          <div className="drive-file-list__cell">
            <Tooltip
              title={record.description}
              overlayClassName="drive-file-list__tooltip"
            >
            <span className="drive-file-list__ellipsis">
              {record.description}
            </span>
            </Tooltip>
          </div>
        );
      }
    },
    {
      title: '文件信息',
      key: 'fileInfo',
      render: (_: any, record: FileItem) => (
        <div className="drive-file-list__info">
          <div>
            大小：{formatFileSize(record.size || record.total_size) || '-'}
          </div>
          <div>
            操作人：{record.update_by || record.owner_email || '-'}
          </div>
          <div>
            修改时间：{record.updated_at || record.last_updated || '-'}
          </div>
        </div>
      )
    },
    {
      title: '操作',
      width: 130,
      fixed: 'right',
      render: (_: any, record: FileItem) => {
        const isRootDirectory = isOrganizationLevel && record.type === 'directory';
        const isShared = !!record.is_shared;

        const canEdit = !isRootDirectory && !isShared && canOperate(record) && !record.is_group;
        const canShareScope = canEdit;

        const menuItems: any[] = [
          canEdit
            ? {
              key: 'rename',
              icon: <FormOutlined/>,
              label: '重命名',
              onClick: async () => {
                const pass = await checkBeforeOperate(record);

                if (!pass) return;

                onRename({
                  id: record.id,
                  type: record.type,
                  name: record.name,
                });
              }
            }
            : null,
          canEdit
            ? {
              key: 'move',
              icon: <SwapOutlined/>,
              label: '移动',
              onClick: async () => {
                const pass = await checkBeforeOperate(record);

                if (!pass) return;

                onMove({
                  id: record.id,
                  type: record.type,
                });
              }
            }
            : null,
          canShareScope
            ? {
              key: 'shareScope',
              icon: <ExportOutlined/>,
              label: '共享',
              onClick: async () => {
                const pass = await checkBeforeOperate(record);

                if (!pass) return;

                onShareScope?.(record);
              }
            }
            : null,
          record.type === 'file'
            ? {
              key: 'shareLink',
              icon: <ShareAltOutlined/>,
              label: '分享',
              onClick: () => shareFile(record)
            }
            : null,
          canEdit
            ? {
              key: 'delete',
              icon: <DeleteOutlined/>,
              label: '删除',
              danger: true,
              onClick: async () => {
                const pass = await checkBeforeOperate(record);

                if (!pass) return;

                Modal.confirm({
                  title: '确认删除？',
                  content: '删除后不可恢复',
                  okText: '删除',
                  okType: 'danger',
                  onOk: () => {
                    onDelete([{
                      id: record.id,
                      type: record.type,
                    }]);
                  }
                });
              }
            }
            : null,
        ].filter(Boolean);

        return (
          <div className="drive-file-list__actions" onClick={(e) => e.stopPropagation()}>
            {record.type === 'directory' && (
              <Tooltip title="打开">
                <Button
                  type="text"
                  size="small"
                  icon={<FolderOpenOutlined/>}
                  className="drive-file-list__icon-btn"
                  onClick={() => {
                    onEnterDirectory({
                      id: record.id,
                      name: record.name,
                      path: buildChildPath(currentPath, record.name),
                    });
                  }}
                />
              </Tooltip>
            )}
            {record.type === 'file' && (
              <>
                {canEdit && (
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined/>}
                    className="drive-file-list__icon-btn"
                    onClick={async () => {
                      const pass = await checkBeforeOperate(record);

                      if (!pass) return;

                      onEdit(record);
                    }}
                  />
                )}
                <DownloadButton file={record}/>
              </>
            )}
            {!!menuItems.length && (
              <Dropdown
                trigger={['click']}
                overlay={<Menu items={menuItems}/>}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<MoreOutlined/>}
                  className="drive-file-list__icon-btn"
                />
              </Dropdown>
            )}
          </div>
        );
      }
    }
  ].filter(Boolean), [onDelete, onMove, onRename, onEdit, onView, onUpdateFileMeta, checkBeforeOperate, shareFile, currentPath, onEnterDirectory, isOrganizationLevel, isMyFileLevel, canOperate, canUpdatePermission, onShareScope]);

  const rowSelection = {
    selectedRowKeys: selectedItems.map((i: FileItem) => `${i.type}-${i.id}`),

    getCheckboxProps: (record: FileItem) => {
      const isRootDirectory = isOrganizationLevel && record.type === 'directory';

      const disabled = isRootDirectory || record.is_shared || !canOperate(record);

      return {
        disabled,
      };
    },

    onChange: (_: any, rows: FileItem[]) => {
      const operableRows = rows.filter(record => {
        const isRootDirectory =
          isOrganizationLevel && record.type === 'directory';

        return !isRootDirectory && !record.is_shared && canOperate(record);
      });

      selectAll(operableRows.map(r => ({
        id: r.id,
        type: r.type,
      })));
    }
  };

  const tableRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const body = tableRef.current?.querySelector('.ant-table-body');

    if (!body) return;

    function handleScroll() {
      const {scrollTop, scrollHeight, clientHeight} = body;

      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      // 距离底部还有 80px 不触发
      if (distanceToBottom > 80) return;

      // 没有下一页
      if (!hasNextPage) return;

      // react-query 正在请求
      if (isFetchingNextPage) return;

      // 本地锁,防止瞬间连续触发
      if (loadingMoreRef.current) return;

      loadingMoreRef.current = true;

      fetchNextPage()
        .finally(() => {
          loadingMoreRef.current = false;
        });
    }

    body.addEventListener('scroll', handleScroll);

    return () => {
      body.removeEventListener('scroll', handleScroll);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const currentDirectory = data?.pages?.[0]?.current_directory;

  useEffect(() => {
    if (!currentDirectory) return;

    onDirectoryLoaded?.(currentDirectory);
  }, [currentDirectory, onDirectoryLoaded]);

  const hasData = list.length > 0;

  if (isLoading && !list.length) {
    return (
      <div className="drive-file-list">
        <Skeleton active paragraph={{rows: 8}}/>
      </div>
    );
  }

  return (
    <div
      ref={tableRef}
      className="drive-file-list"
    >
      <Table
        rowKey={(record: FileItem) => `${record.type}-${record.id}`}
        columns={columns}
        dataSource={list}
        pagination={false}
        rowSelection={rowSelection}
        scroll={hasData ? {y: scrollY, x: 'max-content'} : undefined}
        onRow={(record: FileItem) => ({
          onDoubleClick: () => {
            if (record.type !== 'directory') return;

            onEnterDirectory?.({
              id: record.id,
              name: record.name,
              path: buildChildPath(currentPath, record.name)
            });
          }
        })}
      />

      {showSyncing && (
        <div className="drive-file-list__syncing">
          <LoadingOutlined spin/>
          <span>正在更新...</span>
        </div>
      )}
    </div>
  );
}
